import React from 'react';
import '../css/AchievementCard.css';


const AchievementCard = ({ userAchievement }) => {
    const achievement = userAchievement.achievement;

    if (!achievement) return null;

    const getRarityColor = (rarity) => {
        switch (rarity) {
            case 'legendary': return '#FFD700';
            case 'epic': return '#eb4b4b';
            case 'rare': return '#9932CC';
            case 'common':
            default: return '#0080FF';
        }
    };

    const formatRewards = (rewards) => {
        const rewardParts = [];
        if (rewards.coins > 0) rewardParts.push(`${rewards.coins} coins`);
        if (rewards.cosmetic) {
            switch (rewards.cosmetic.type) {
                case 'theme': rewardParts.push(`Theme: "${rewards.cosmetic.name}"`); break;
                case 'cardBackground': rewardParts.push(`Card Background: "${rewards.cosmetic.name}"`); break;
                case 'cardOutline': rewardParts.push(`Card Outline: "${rewards.cosmetic.name}"`); break;
                default: rewardParts.push('Cosmetic');
            }
        }
        if (rewards.title) rewardParts.push(`Title: "${rewards.title}"`);
        if (rewards.pack) rewardParts.push('Pack');
        return rewardParts.join(' • ');
    };

    return (
        <div
            className={`achievement-card ${userAchievement.isCompleted ? 'completed' : 'incomplete'}`}
        >
            {/* Rarity indicator */}
            <div
                className="achievement-card-rarity-indicator"
                style={{ backgroundColor: getRarityColor(achievement.rarity) }}
                title={`${achievement.rarity} achievement`}
            />

            {/* Header */}
            <div className="achievement-card-header">
                <div
                    className={`achievement-card-icon ${achievement.icon}`}
                    title={achievement.name}
                />
                <div className="achievement-card-header-content">
                    <h6 className={`achievement-card-title ${userAchievement.isCompleted ? 'completed' : 'incomplete'}`}>
                        {achievement.name}
                        {userAchievement.isCompleted && <span className="achievement-card-checkmark">✓</span>}
                    </h6>
                    <small className="achievement-card-subtitle">
                        {achievement.category} • {achievement.rarity}
                    </small>
                </div>
            </div>

            {/* Description */}
            <p className="achievement-card-description">
                {achievement.description}
            </p>

            {/* Progress Bar */}
            <div className="achievement-card-progress-container">
                <div className="achievement-card-progress-text">
                    <span>Progress: {userAchievement.progress.current.toFixed(2)}/{userAchievement.progress.target.toFixed(0)}</span>
                    <span>{userAchievement.progress.percentage.toFixed(1)}%</span>
                </div>
                <div className="achievement-card-progress-bar">
                    <div
                        className={`achievement-card-progress-fill ${userAchievement.isCompleted ? 'completed' : 'incomplete'}`}
                        style={{ width: `${Math.min(userAchievement.progress.percentage, 100)}%` }}
                    ></div>
                </div>
            </div>

            {/* Rewards */}
            {achievement.rewards && (
                <div className="achievement-card-rewards">
                    <strong>Rewards:</strong> {formatRewards(achievement.rewards)}
                </div>
            )}

            {/* Season info for season-specific achievements */}
            {userAchievement.season && (
                <div className="achievement-card-season">
                    <strong>Season:</strong> {userAchievement.season.name || 'Specific Season'}
                </div>
            )}

            {/* Unlock Date */}
            {userAchievement.isCompleted && userAchievement.unlockedAt && (
                <div className="achievement-card-unlock-date">
                    <strong>Unlocked:</strong> {new Date(userAchievement.unlockedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </div>
            )}
        </div>
    );
};

const AchievementStats = ({ stats, loading }) => {
    if (loading) {
        return (
            <div className='profile-stats-table-container'>
                <div className='loader-overlay'>
                    <div className='loader'></div>
                </div>
            </div>
        );
    }

    return (
        <div className='profile-stats-table-container'>
            <table className='profile-stats-table'>
                <thead>
                    <tr>
                        <th>Achievement Stats</th>
                        <th>Value</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Total Achievements</td>
                        <td>{stats.total}</td>
                    </tr>
                    <tr>
                        <td>Completed</td>
                        <td className="completed-stat">{stats.completed}</td>
                    </tr>
                    <tr>
                        <td>In Progress</td>
                        <td className="in-progress-stat">{stats.inProgress}</td>
                    </tr>
                    <tr>
                        <td>Not Started</td>
                        <td>{stats.notStarted}</td>
                    </tr>
                    <tr>
                        <td>Completion Rate</td>
                        <td className={stats.completionRate > 50 ? 'completion-rate-high' : 'completion-rate-low'}>
                            {stats.completionRate.toFixed(1)}%
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

const AchievementGrid = ({ achievements, loading, error }) => {
    if (loading) {
        return (
            <div className='fantasyTeamView-content' style={{ minHeight: '30vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className='loader-overlay' style={{ backgroundColor: 'rgba(255, 255, 255, 0)' }}>
                    <div className='loader'></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className='error achievements-error'>
                <p>Unable to load achievements: {error}</p>
            </div>
        );
    }

    if (achievements.length === 0) {
        return (
            <p className='achievements-empty'>
                No achievements found for this selection.
            </p>
        );
    }

    return (
        <div className='achievements-grid'>
            {achievements.map(userAchievement => (
                <AchievementCard key={userAchievement._id} userAchievement={userAchievement} />
            ))}
        </div>
    );
};

export { AchievementCard, AchievementStats, AchievementGrid };
export default AchievementCard;
